import { Button, Card, Flex, Text } from "@radix-ui/themes";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import type { Session } from "../lib/auth";
import { authClient } from "../lib/auth";

const API_BASE = import.meta.env.VITE_API_URL ?? "/api";
const DIRECT_DOCS_BASE = (
  import.meta.env.VITE_THREAD_DOCS_DIRECT_BASE ?? ""
).replace(/\/$/, "");
const USE_DIRECT_DOCS = import.meta.env.VITE_THREAD_ENV === "dev" && DIRECT_DOCS_BASE;

type DocsState =
  | { stage: "loading" }
  | { stage: "ready"; url: string; email?: string }
  | { stage: "needs-thread-session" }
  | { stage: "error"; message: string };

function docsPathFromLocation(location: ReturnType<typeof useLocation>): string {
  const withoutPrefix = location.pathname.replace(/^\/docs\/?/, "");
  return withoutPrefix ? `/${withoutPrefix}` : "/";
}

function buildInternalDocsUrl(path: string, search: string, token: string): string {
  const separator = search ? "&" : "?";
  return `${API_BASE}/internal-docs${path}${search}${separator}token=${encodeURIComponent(token)}`;
}

function buildDirectDocsUrl(path: string, search: string, hash: string): string {
  return `${DIRECT_DOCS_BASE}${path}${search}${hash}`;
}

function currentAppPath(location: ReturnType<typeof useLocation>): string {
  return `${location.pathname}${location.search}${location.hash}`;
}

export default function DocsPage() {
  const location = useLocation();
  const docsPath = useMemo(() => docsPathFromLocation(location), [location.pathname]);
  const [state, setState] = useState<DocsState>({ stage: "loading" });

  useEffect(() => {
    let cancelled = false;

    authClient.auth.getSession().then(({ data }: { data: { session: Session | null } }) => {
      if (cancelled) return;
      const token = data.session?.access_token;
      if (!token) {
        setState({ stage: "needs-thread-session" });
        return;
      }
      setState({
        stage: "ready",
        url: USE_DIRECT_DOCS
          ? buildDirectDocsUrl(docsPath, location.search, location.hash)
          : buildInternalDocsUrl(docsPath, location.search, token),
        email: data.session?.user?.email,
      });
    }).catch((err) => {
      if (!cancelled) {
        setState({ stage: "error", message: String(err) });
      }
    });

    return () => {
      cancelled = true;
    };
  }, [docsPath, location.hash, location.search]);

  const signInWithGoogle = async () => {
    const base = (import.meta.env.BASE_URL || "/").replace(/\/$/, "");
    const redirectTo = `${window.location.origin}${base}/auth/callback?next=${encodeURIComponent(currentAppPath(location))}`;
    const { error } = await authClient.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo },
    });
    if (error) {
      setState({ stage: "error", message: error.message });
    }
  };

  if (state.stage === "ready") {
    return (
      <iframe
        src={state.url}
        title={state.email ? `Thread docs for ${state.email}` : "Thread docs"}
        style={{
          position: "fixed",
          inset: 0,
          width: "100vw",
          height: "100vh",
          border: 0,
          background: "#0a1a12",
        }}
      />
    );
  }

  return (
    <Flex
      justify="center"
      align="center"
      style={{ minHeight: "100vh", background: "var(--color-background)", padding: 24 }}
    >
      <Card size="4" style={{ width: 420, padding: 32 }}>
        <Flex direction="column" gap="4" align="center">
          <Text size="5" weight="bold">
            Thread Docs
          </Text>
          {state.stage === "loading" && (
            <Text size="2" color="gray">Loading internal docs…</Text>
          )}
          {state.stage === "needs-thread-session" && (
            <>
              <Text size="2" color="gray" align="center" style={{ lineHeight: 1.5 }}>
                Internal docs require a Flexcompute Thread session.
              </Text>
              <Button size="3" variant="surface" onClick={signInWithGoogle}>
                Continue with Google
              </Button>
            </>
          )}
          {state.stage === "error" && (
            <Text size="2" color="red" align="center" style={{ lineHeight: 1.5 }}>
              {state.message}
            </Text>
          )}
        </Flex>
      </Card>
    </Flex>
  );
}
